import { FIRM, firmSavedPreviewApi, isUserVerifiedApi } from 'Constants';
import { Context } from 'Context/Context';
import React, { useContext, useEffect, useState } from 'react'
import { Bookmark, Clock, UserRoundPen, XCircle } from 'lucide-react';
import ReactModal from 'react-modal'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { getCaseType } from 'Utils'
import { estimateOf } from 'Components/Professional/Cases/CaseCard'

// Shown on the case lists when the attorney's profile isn't verified yet.
// verified: 2 = under review, 5 = not approved, anything else = incomplete.
const STATE = {
  2: {
    icon: Clock, tone: 'bg-brass/[.1] text-brass',
    title: 'Your profile is under review',
    text: "Saved cases are unlocked after your profile is verified. We'll let you know as soon as it's approved.",
    button: 'View profile', to: '/attorney/profile',
  },
  5: {
    icon: XCircle, tone: 'bg-danger/[.08] text-danger',
    title: "Your profile wasn't approved",
    text: 'Read the review message on your profile, update your details and resubmit.',
    button: 'View profile', to: '/attorney/profile',
  },
  incomplete: {
    icon: UserRoundPen, tone: 'bg-brass/[.1] text-brass',
    title: 'Complete your profile',
    text: 'We need a few details to verify your account before you can view cases.',
    button: 'Complete profile', to: '/attorney/profileCompletion',
  },
};

// One-line verification status shown under a firm's saved cases (popup and
// the unverified saved-cases list). verified: 2 = under review, 5 = not approved.
export const verificationStatusLine = (verified) => verified === 5
  ? { icon: XCircle, tone: 'bg-danger/[.06] border-danger/[.25]', iconTone: 'text-danger',
      bold: "Your profile wasn't approved.", rest: "Update your details and resubmit. Once it's verified, you can request to view more details.",
      button: 'View profile', to: '/attorney/profile' }
  : verified === 2
    ? { icon: Clock, tone: 'bg-brass/[.07] border-brass/[.25]', iconTone: 'text-brass',
        bold: 'Your profile is under review.', rest: "You can request to view more details once it's verified.",
        button: 'View profile', to: '/attorney/profile' }
    : { icon: UserRoundPen, tone: 'bg-brass/[.07] border-brass/[.25]', iconTone: 'text-brass',
        bold: 'Complete your profile.', rest: "Once it's verified, you can request to view more details.",
        button: 'Complete profile', to: '/attorney/profileCompletion' };

export const capWords = (v) => (v ? String(v).replace(/(^|\s)\S/g, (c) => c.toUpperCase()) : '');
const cap = (v) => (v ? String(v).replace(/(^|\s)\S/g, (c) => c.toUpperCase()) : '');

// Law firms: the cases clients saved their firm on, as the same short summary
// the outreach email shows (type · place · posted date · estimate).
function SavedCasesPreview({ preview, showHeading = true }) {
  if (!preview?.total) return null;
  const more = preview.total - (preview.cases?.length || 0);
  return (
    <div className='w-full text-left mt-1 mb-4'>
      {showHeading &&
        <div className='text-[11px] font-semibold tracking-[.12em] uppercase text-brass mb-2'>
          {preview.total === 1 ? 'A client saved your firm' : `${preview.total} clients saved your firm`}
        </div>}
      <div className='flex flex-col gap-2'>
        {preview.cases.map((c) => {
          const place = [cap(c.caseCity), c.caseState].filter(Boolean).join(', ');
          const est = estimateOf(c);
          return (
            <div key={c._id} className='bg-white border border-navy/[.1] border-l-[3px] border-l-brass rounded-md py-2.5 px-3'>
              <div className='text-[14px] font-semibold leading-[1.35] text-navy'>{getCaseType(c.caseType) || 'Case'}</div>
              <div className='text-[12.5px] leading-[1.5] text-navy/[.6] mt-0.5'>
                {[place, c.created_at ? 'Posted ' + moment(c.created_at).format('MMM D, YYYY') : null].filter(Boolean).join(' · ')}
              </div>
              {est && <div className='text-[12.5px] font-semibold leading-[1.5] text-brass mt-1'>Est. {est}</div>}
            </div>
          );
        })}
      </div>
      {more > 0 && <div className='text-[12.5px] text-navy/[.55] mt-2'>+ {more} more</div>}
    </div>
  );
}

function UserNotVerifiedModal({uploadNew, onClose}) {
  const [userVerificationId, setUserVerificationId] = useState(null);
  const [preview, setPreview] = useState(null);
  const {makeApiCallFunc, userDetails} = useContext(Context);
  const isFirm = userDetails?.role === FIRM;
  useEffect(() => {
    if (!uploadNew || !isFirm) return;
    makeApiCallFunc(firmSavedPreviewApi, {}, ({data}) => setPreview(data || { total: 0, cases: [] }), () => setPreview({ total: 0, cases: [] }), null, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[uploadNew, isFirm])
  useEffect(() => {
    makeApiCallFunc(
                  isUserVerifiedApi,
                  {verified: userDetails?.verified},
                  ({data}) => {
                    setUserVerificationId(data?.verified);
                  },
                  (data) => {
                    console.log(data)
                  },
                  null, true  // quiet: the popup simply opens once it's ready
                )
  },[uploadNew])

  const s = STATE[userVerificationId] || STATE.incomplete;
  const Icon = s.icon;
  // Under review + clients already saved this firm: lead with the saved cases,
  // then explain that they open once the profile is verified.
  // (also used when the profile wasn't approved, with a different status line)
  const savedWhileInReview = preview?.total > 0;
  const statusLine = verificationStatusLine(userVerificationId);
  const StatusIcon = statusLine.icon;
  // law firms: wait for the saved-cases check too, so the plain "under review"
  // popup doesn't flash before the saved-cases version
  const ready = userVerificationId !== null && (!isFirm || preview !== null);
  const many = preview?.total > 1;
  return (
    <ReactModal
    isOpen = {!!uploadNew && ready}
    style={{
        overlay: { zIndex: 30, backgroundColor: 'rgba(20,33,61,.45)' },
        content: {
            height: 'auto', width: 'calc(100vw - 32px)', maxWidth: preview?.total ? '460px' : '420px', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
            top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)',
            padding: '28px 24px 24px', border: '1px solid rgba(20,33,61,.12)', borderRadius: '8px',
            backgroundColor: '#fffdf8', boxShadow: '0 16px 40px rgba(20,33,61,.3)',
        }
      }}
    >
    {ready &&
        <div className='flex flex-col items-center text-center font-plexsans'>
          {savedWhileInReview ? (
            <>
              <div className='flex items-center justify-center rounded-full mb-4 w-12 h-12 bg-brass/[.1] text-brass'>
                <Bookmark size={21} strokeWidth={1.8}/>
              </div>
              <div className='font-spectral font-medium text-[20px] leading-[1.3] text-navy mb-2'>
                {many ? `${preview.total} clients saved your firm` : 'A client saved your firm'}
              </div>
              <p className='m-0 mb-4 text-[13.5px] leading-[1.6] text-navy/[.6]'>
                {many ? "They'd like to hear from you about these cases." : "They'd like to hear from you about this case."}
              </p>
              <SavedCasesPreview preview={preview} showHeading={false} />
              <div className={`w-full flex items-start gap-2.5 text-left rounded-md border py-2.5 px-3 ${statusLine.tone}`}>
                <StatusIcon size={16} strokeWidth={2} className={`flex-none mt-[2px] ${statusLine.iconTone}`}/>
                <div className='text-[13px] leading-[1.5] text-navy'>
                  <span className='font-semibold'>{statusLine.bold}</span>{' '}
                  <span className='text-navy/[.7]'>{statusLine.rest}</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={`flex items-center justify-center rounded-full mb-4 w-12 h-12 ${s.tone}`}>
                <Icon size={22} strokeWidth={1.8}/>
              </div>
              <div className='font-spectral font-medium text-[20px] leading-[1.3] text-navy mb-3'>{s.title}</div>
              <SavedCasesPreview preview={preview} />
              <p className='m-0 text-[13.5px] leading-[1.6] text-navy/[.6]'>{s.text}</p>
            </>
          )}
          <Link to={s.to} className='block w-full mt-6'>
            <span className='w-full flex justify-center items-center h-11 rounded-md font-semibold text-[14px] leading-none text-ivory bg-brass hover:bg-brassDark'>
              {s.button}
            </span>
          </Link>
        </div>
    }
    </ReactModal>
  )
}

export default UserNotVerifiedModal
